/**
 * v2's list saga: one worker for all ten lists (the nine tabs plus search), because the action
 * carries the `storeKey`. v1 needs nine request/success pairs and a separate search worker.
 *
 * ⚠️ `takeLatest` keys on the ACTION TYPE, so one shared action under one `takeLatest` would
 * cancel across lists: the notification refetch of "new orders" would kill an in-flight overview
 * fetch. `watchOrdersFetch` keeps one in-flight task PER `storeKey` instead.
 *
 * This is v2's only saga. The mutations are async functions in `./api`. The list stays a saga
 * because other flows dispatch it and it needs latest-wins cancellation. The offline list is
 * `./localOrders`, which never touches the network.
 */
import { call, cancel, fork, put, take } from "redux-saga/effects";
import type { Task } from "redux-saga";
import api from "../utils/apiService";
import { ENDPOINT } from "../config/endpoints";
import { ordersFailed, ordersReceived, ordersRequested } from "./slice";

/**
 * `call([api, api.request], …)` rather than `yield api.request(…)`: the same request, but the
 * effect is a value a test can assert without a network. The context form keeps axios's `this`.
 */
const request = (config: Record<string, any>) => call([api, api.request], config as any);

/** logs and swallows the error, as v1's workers did; `ordersFailed` stops the spinner */
const logged = (error: any) => console.log("error", error);

export function* fetchOrdersWorker(action: any): Generator<any, void, any> {
  // the payload IS `buckets.listPayload(...)` plus the storeKey — the loader keys keep their one
  // definition there (web's interceptor reads `skipGlobalLoader`, mobile's `hideLoader`)
  const { storeKey, data, skipGlobalLoader, hideLoader } = action.payload ?? {};
  try {
    const response = yield request({
      method: "POST",
      url: ENDPOINT.MYORDER.getMyOrders(),
      data,
      skipGlobalLoader,
      hideLoader,
    });
    yield put(ordersReceived({ storeKey, response }));
  } catch (error: any) {
    logged(error);
    yield put(ordersFailed({ storeKey }));
  }
}

/** One in-flight fetch per list: a second request for the SAME `storeKey` cancels the first. */
export function* watchOrdersFetch(): Generator<any, void, any> {
  const running: Record<string, Task> = {};
  while (true) {
    const action = yield take(ordersRequested.type);
    const storeKey: string = action.payload?.storeKey;
    if (!storeKey) continue;
    const previous = running[storeKey];
    if (previous) yield cancel(previous);
    running[storeKey] = yield fork(fetchOrdersWorker, action);
  }
}

export default function* ordersV2Saga(): Generator<any, void, any> {
  yield* watchOrdersFetch();
}
